'use client';

import { useUserDataStore } from '@/stores/userDataStore';
import {
  NotificationChannelClient,
  notificationChannelClient,
} from '@/utils/NotificationChannelClient';
import React, {
  createContext,
  useContext,
  ReactNode,
  useRef,
  useEffect,
} from 'react';
import { useShallow } from 'zustand/react/shallow';

type NotificationChannelContextType = NotificationChannelClient;

const NotificationChannelContext = createContext<
  NotificationChannelContextType | undefined
>(undefined);

export const useNotificationChannel = () => {
  const context = useContext(NotificationChannelContext);
  if (!context) {
    throw new Error(
      'useNotificationChannel must be used within a NotificationChannelProvider'
    );
  }
  return context;
};

interface NotificationChannelProviderProps {
  children: ReactNode;
}

export const NotificationChannelProvider: React.FC<
  NotificationChannelProviderProps
> = ({ children }) => {
  const hasRunWithAppKey = useRef<string | null>(null);
  const { appKey } = useUserDataStore(
    useShallow((state) => ({ appKey: state.appKey }))
  );
  useEffect(() => {
    if (typeof window === 'undefined' || hasRunWithAppKey.current === appKey) {
      return;
    }
    hasRunWithAppKey.current = appKey;
    if (appKey) {
      notificationChannelClient.connect(appKey);
    } else {
      notificationChannelClient.disconnect();
    }
    return () => {
      notificationChannelClient.disconnect();
    };
  }, [appKey]);

  return (
    <NotificationChannelContext.Provider value={notificationChannelClient}>
      {children}
    </NotificationChannelContext.Provider>
  );
};
